import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Camera as CameraIcon } from 'lucide-react';
import { cameraService } from '../../services/camera.service';
import type { Camera, CameraStream } from '../../types';
import EZOPENPlayer, { EZOPENPlayerHandle } from '../ui/EZOPENPlayer';
import HLSPlayer from '../ui/HLSPlayer';
import Button from '../ui/Button';

export interface CameraPhotoCaptureHandle { capture: () => Promise<Blob>; }
interface Props { cameraId: number; label: string; onCapture: (file: File) => void; }
interface Source { camera: Camera; kind: 'cloud' | 'player' | 'hls' | 'image' | 'video'; url?: string; }

function streamSource(camera: Camera, stream: CameraStream): Source {
  const player = stream.player_url || stream.web_player_url || stream.web_player_url_external;
  const hls = stream.hls_url_external || stream.hls_url;
  const image = stream.mjpeg_url;
  const video = stream.mp4_url;
  const fallback = stream.webrtc_url_external || stream.webrtc_url || stream.stream_url;
  const kind = player ? 'player' : hls ? 'hls' : image ? 'image' : video ? 'video' : 'player';
  const url = player || hls || image || video || fallback;
  if (!url || !/^https?:\/\//i.test(url) || /\/whep\/?(?:\?|$)/i.test(url)) {
    throw new Error('No browser-compatible camera preview is configured. Check this camera’s streaming settings.');
  }
  if (window.location.protocol === 'https:' && url.startsWith('http:')) {
    throw new Error('This camera preview requires an HTTPS stream URL. Update the location’s streaming settings.');
  }
  return { camera, kind, url };
}

const CameraPhotoCapture = forwardRef<CameraPhotoCaptureHandle, Props>(({ cameraId, label, onCapture }, ref) => {
  const [source, setSource] = useState<Source | null>(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const player = useRef<EZOPENPlayerHandle>(null);
  const alive = useRef(false);
  const selected = useRef(cameraId); selected.current = cameraId;
  const abort = useRef<AbortController | null>(null);
  const takingPhoto = useRef(false);

  useEffect(() => {
    let active = true;
    alive.current = true;
    setSource(null); setReady(false); setError('');
    const load = async () => {
      try {
        // List rows may omit cloud details. Always use the full camera record.
        const camera = await cameraService.getCameraById(cameraId);
        if (!active) return;
        const next: Source = camera.cloud_provider
          ? { camera, kind: 'cloud' }
          : streamSource(camera, await cameraService.getCameraStream({ camera_id: cameraId, stream_type: 'webrtc', stream_quality: 'main' }));
        if (active) setSource(next);
      } catch (error) { if (active) setError(error instanceof Error ? error.message : 'Unable to connect to this camera.'); }
    };
    void load();
    return () => { active = false; alive.current = false; abort.current?.abort(); };
  }, [cameraId, attempt]);

  const capture = async () => {
    if (!source || source.camera.camera_id !== cameraId || !ready) throw new Error('Wait for the camera preview before capturing.');
    if (takingPhoto.current) throw new Error('A photo is already being captured.');
    takingPhoto.current = true;
    const controller = new AbortController(); abort.current = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      let blob: Blob;
      if (source.kind === 'cloud') {
        if (!player.current) throw new Error('The camera player is not ready. Retry the camera connection.');
        blob = await player.current.captureSnapshot();
      } else {
        // Fetch through the authenticated API, which can use NVR/DDNS. Browser
        // requests to raw camera snapshot URLs fail on authentication or CORS.
        const response = await cameraService.getCameraSnapshot(cameraId, 'high', controller.signal);
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(typeof result.detail === 'string' ? result.detail : `Camera capture failed (${response.status}).`);
        }
        blob = await response.blob();
      }
      if (!blob.size || !blob.type.startsWith('image/')) throw new Error('The camera did not return a valid image.');
      if (!alive.current || selected.current !== cameraId || controller.signal.aborted) throw new Error('Capture cancelled. Select the camera and try again.');
      return blob;
    } finally { clearTimeout(timeout); takingPhoto.current = false; if (abort.current === controller) abort.current = null; }
  };
  useImperativeHandle(ref, () => ({ capture }));

  const takePhoto = async () => {
    setBusy(true); setError('');
    try {
      const blob = await capture();
      onCapture(new File([blob], `capture-${cameraId}-${Date.now()}.${blob.type === 'image/png' ? 'png' : 'jpg'}`, { type: blob.type }));
    } catch (error) {
      if (alive.current && selected.current === cameraId) setError(error instanceof Error ? error.message : 'Unable to capture the photo.');
    } finally { if (alive.current && selected.current === cameraId) setBusy(false); }
  };
  const previewError = () => { setReady(false); setError('Unable to load this camera. Check its connection and retry.'); };
  return <div className="mt-4 space-y-3">
    <div className="relative h-[300px] overflow-hidden rounded-lg bg-black" aria-label={`${label} preview`}>
      {!source && !error && <p role="status" className="flex h-full items-center justify-center text-sm text-white">Connecting to camera…</p>}
      {source?.kind === 'cloud' && <EZOPENPlayer key={`${cameraId}-${attempt}`} ref={player} camera={{ id: cameraId, ...source.camera }} mode="live" fillContainer showControls={false} showAudioControls={false} onReady={() => setReady(true)} onError={previewError} />}
      {source?.kind === 'player' && <iframe src={source.url} title={`${label} live camera`} className="h-full w-full border-0" allow="autoplay" onLoad={() => setReady(true)} onError={previewError} />}
      {source?.kind === 'hls' && <div className="h-full" onCanPlay={() => setReady(true)}><HLSPlayer url={source.url!} autoPlay muted controls={false} className="h-full w-full" onError={previewError} /></div>}
      {source?.kind === 'image' && <img src={source.url} alt={`${label} live camera`} className="h-full w-full object-contain" onLoad={() => setReady(true)} onError={previewError} />}
      {source?.kind === 'video' && <video src={source.url} autoPlay muted playsInline className="h-full w-full object-contain" onCanPlay={() => setReady(true)} onError={previewError} />}
    </div>
    {error && <div role="alert" className="text-sm text-status-danger">{error}<Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setAttempt(value => value + 1)}>Retry camera</Button></div>}
    <Button type="button" className="w-full" disabled={!ready || busy} isLoading={busy} leftIcon={<CameraIcon size={16} />} onClick={takePhoto}>{busy ? 'Capturing…' : label}</Button>
  </div>;
});
CameraPhotoCapture.displayName = 'CameraPhotoCapture';
export default CameraPhotoCapture;
